import { useEffect } from "react";
import { create } from "zustand";

/**
 * How many open layers sit under the dock with controls where the dock normally docks (the post dialog: its close
 * button and menu at the top on a phone, the comments input bottom-right on desktop; the composer: Add link and Post
 * at its foot). While any is open the dock
 * re-anchors out of their way (StreamDock) and goes back when the last one closes.
 */
/** Where the dock's mini player (or the phone dock moved to the bottom) sits, viewport px. */
export interface DockFrameRect {
  left: number;
  right: number;
  top: number;
}

interface DockAvoidState {
  count: number;
  /**
   * Phone only, while avoiding: how far (px) the dock, moved to the bottom, reaches up over the foot of the panel
   * above the tab bar. The in-panel composer pads its end by it so Post / Cancel scroll clear. 0 when no dock covers it.
   */
  bottomInset: number;
  /**
   * The dock frame's rect while it sits over the screen's foot (the mini player, phone or desktop; the phone dock moved
   * to the bottom). Chat inputs pad clear of it only where they overlap it (useDockClearance). Null otherwise.
   */
  frame: DockFrameRect | null;
  open(): void;
  close(): void;
  setBottomInset(px: number): void;
  setFrame(rect: DockFrameRect | null): void;
}

export const useDockAvoidStore = create<DockAvoidState>((set) => ({
  count: 0,
  bottomInset: 0,
  frame: null,
  open: () => set((s) => ({ count: s.count + 1 })),
  close: () => set((s) => ({ count: Math.max(0, s.count - 1) })),
  setBottomInset: (px) => set((s) => (s.bottomInset === px ? s : { bottomInset: px })),
  setFrame: (rect) =>
    set((s) =>
      s.frame === rect || (s.frame && rect && s.frame.left === rect.left && s.frame.right === rect.right && s.frame.top === rect.top)
        ? s
        : { frame: rect },
    ),
}));

/** Registers the calling component as a layer the dock moves away from while it is mounted. */
export function useDockAvoid(active = true): void {
  useEffect(() => {
    if (!active) return;
    useDockAvoidStore.getState().open();
    return () => useDockAvoidStore.getState().close();
  }, [active]);
}

/** Tests: nothing to avoid. */
export const resetDockAvoidStore = (): void => useDockAvoidStore.setState({ count: 0, bottomInset: 0, frame: null });
